import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import {
  StorageLocationId,
  storageLocationIdFormat,
} from "@redotech/redo-model/ids";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";

export interface BinSourceItem {
  productId: string;
  productTitle: string;
  variantTitle: string | null;
  sku: string | null;
  externalId: string;
  requiredQuantity: number;
  bins: {
    storageLocationId: string;
    binName: string;
    availableQuantity: number;
    lastCountedAt?: Date | null;
  }[];
}

export interface BinSourceSelectionModalData {
  item: BinSourceItem;
  fulfillmentGroupId: string;
  transferSessionId: string | null;
  pickSessionId: string | null;
  onConfirm: () => void;
  onSkip: () => void;
}

interface BinSourceSelectionModalProps {
  data: BinSourceSelectionModalData;
  isOpen: boolean;
  onClose: () => void;
}

export const BinSourceSelectionModal = memo(function BinSourceSelectionModal({
  data,
  isOpen,
  onClose,
}: BinSourceSelectionModalProps) {
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const [selectedBinId, setSelectedBinId] = useState<string | null>(null);
  const [isReceive, setIsReceive] = useState(false);

  const selectedBin = data.item.bins.find(
    (b) => b.storageLocationId === selectedBinId,
  );
  const isUncountedBin =
    selectedBin &&
    (selectedBin.lastCountedAt === null ||
      selectedBin.lastCountedAt === undefined);

  const handleSkip = useHandler(() => {
    onClose();
    data.onSkip();
  });

  const [submitLoad, triggerSubmit] = useTriggerLoad(async () => {
    if (!selectedBinId && !isReceive) {
      return;
    }

    try {
      if (!data.transferSessionId) {
        return;
      }

      if (isReceive || isUncountedBin) {
        // For receive or uncounted bins, use receiveFromExternal
        await client.recordAdHocBinPick({
          fulfillmentGroupId: data.fulfillmentGroupId,
          transferSessionId: data.transferSessionId,
          pickSessionId: data.pickSessionId,
          itemExternalId: data.item.externalId,
          binSelections: {},
          receiveFromExternal: true,
        });
      } else {
        const binSelections: Record<
          string,
          { storageLocationId: StorageLocationId; quantity: number }[]
        > = {
          [data.item.productId]: [
            {
              storageLocationId:
                storageLocationIdFormat.schema.parse(selectedBinId),
              quantity: 1,
            },
          ],
        };

        await client.recordAdHocBinPick({
          fulfillmentGroupId: data.fulfillmentGroupId,
          transferSessionId: data.transferSessionId,
          pickSessionId: data.pickSessionId,
          itemExternalId: data.item.externalId,
          binSelections,
        });
      }

      onClose();
      data.onConfirm();
    } catch (error) {
      toast("Failed to record bin source", { variant: "error" });
    }
  });

  const itemLabel = [data.item.productTitle, data.item.variantTitle]
    .filter(Boolean)
    .join(" - ");

  return (
    <RedoModal
      hideFooter
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={handleSkip}
      subtitle={
        data.item.bins.length > 0
          ? "This item was not in the pick list. Select which bin it came from."
          : "This item was not in the pick list and has no known inventory."
      }
      theme="brand"
      title="Where did this come from?"
    >
      <Flex dir="column" gap="xl" pb="3xl">
        <Text fontWeight="semibold">{itemLabel}</Text>

        <Flex dir="column" gap="sm">
          <RedoButton
            hierarchy={isReceive ? "primary" : "secondary"}
            onClick={() => {
              setIsReceive(true);
              setSelectedBinId(null);
            }}
            size="md"
            text="Receive (from external)"
            theme="normal"
          />
          {data.item.bins.map((bin) => {
            const uncounted =
              bin.lastCountedAt === null || bin.lastCountedAt === undefined;
            return (
              <RedoButton
                hierarchy={
                  selectedBinId === bin.storageLocationId
                    ? "primary"
                    : "secondary"
                }
                key={bin.storageLocationId}
                onClick={() => {
                  setSelectedBinId(bin.storageLocationId);
                  setIsReceive(false);
                }}
                size="md"
                text={`${bin.binName} (${uncounted ? `${bin.availableQuantity} available (uncounted)` : `${bin.availableQuantity} available`})`}
                theme="normal"
              />
            );
          })}
        </Flex>

        <Flex align="center" dir="row" gap="md" justify="flex-end" pt="lg">
          <RedoButton
            hierarchy="tertiary"
            onClick={handleSkip}
            size="md"
            text="Skip"
            theme="normal"
          />
          <RedoButton
            disabled={(!selectedBinId && !isReceive) || !data.transferSessionId}
            hierarchy="primary"
            onClick={() => triggerSubmit()}
            pending={submitLoad.pending}
            size="md"
            text="Confirm"
            theme="normal"
          />
        </Flex>
      </Flex>
    </RedoModal>
  );
});
